/**
 * ตรรกะล้วนของหน้าจอแผนกและพนักงาน — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `permissionMatrixView.ts` และ `systemRolesView.ts`: สิ่งที่อยู่ที่นี่
 * คือคำถามที่ตอบได้โดยไม่ต้องเรนเดอร์อะไรเลย — โดยเฉพาะ **การประกอบ body** ซึ่งเป็น
 * ด่านเดียวที่ทางเขียนทั้งสี่เส้นของหน้าจอนี้เดินผ่าน
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่คำนวณ `is_primary`/`is_home_plant` เองเลย** และนั่น
 * เป็นข้อจำกัดโดยเจตนา: ธงทั้งสองเป็น singleton ที่ **เซิร์ฟเวอร์ย้ายให้ในธุรกรรม
 * เดียว** หน้าจอจึงอ่านมันจากคำตอบเท่านั้น `primaryDepartmentOf()`/`homePlantOf()`
 * ด้านล่างเป็นเพียงการ *อ่าน* ชุดที่เซิร์ฟเวอร์ส่งมา ไม่ใช่การคำนวณว่าอันไหนควรเป็น
 */

import type {
  CreateDepartmentBody,
  CreateEmployeeBody,
  Employee,
  EmployeeDepartmentLink,
  EmployeePlantGrant,
  SetEmployeeDepartmentBody,
  SetEmployeePlantAccessBody,
} from '../../lib/rbacAdmin/types';
import {
  ACTIVE_EMPLOYEE_STATUS,
  EMPLOYEE_STATUSES,
  INACTIVE_EMPLOYEE_STATUS,
} from '../../lib/rbacAdmin/types';

export interface EmployeeStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * สองสถานะที่ฟอร์มเสนอให้เลือก — ตามลำดับที่ contract เขียนไว้
 *
 * `INACTIVE` **ไม่ใช่การลบ**: local master ไม่เคยถูกลบจริง เพราะการอ้างถึงผู้กระทำ
 * ในอดีตต้องยังอ่านออกได้เสมอ ข้อความของมันจึงต้องพูดเรื่องนี้ ไม่ใช่แค่บอกชื่อ
 */
export const EMPLOYEE_STATUS_DESCRIPTORS: readonly EmployeeStatusDescriptor[] = [
  {
    code: ACTIVE_EMPLOYEE_STATUS,
    label: 'ใช้งานอยู่ (ACTIVE)',
    summary: 'อ้างเป็นผู้กระทำในธุรกรรมและถูกตรวจสิทธิ์ได้ตามปกติ',
  },
  {
    code: INACTIVE_EMPLOYEE_STATUS,
    label: 'ปิดการใช้งาน (INACTIVE)',
    summary:
      'ไม่ใช่การลบ — ข้อมูลเดิมยังอยู่ครบเพื่อให้การอ้างถึงผู้กระทำในอดีตยังอ่านออกได้',
  },
];

/** ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้น** */
export function employeeStatusLabel(status: string): string {
  return EMPLOYEE_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status;
}

/** สถานะทั้งหมดที่ฟอร์มเสนอ — ใช้ร่วมกันระหว่าง view กับเทสต์ */
export const OFFERED_EMPLOYEE_STATUSES: readonly string[] = EMPLOYEE_STATUSES;

/**
 * ประกอบ body ของ `POST /v1/admin/departments` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * ชื่อถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอแต่ทำให้
 * `uq_department_department_name` มองว่าเป็นชื่อคนละชื่อ — ผลคือสองแผนกที่อ่านแล้ว
 * เหมือนกันทุกตัวอักษร ซึ่งไม่มีใครแยกออกได้อีกเลยหลังจากนั้น
 */
export function toCreateDepartmentBody(name: string): CreateDepartmentBody | null {
  const trimmed = name.trim();
  return trimmed === '' ? null : { department_name: trimmed };
}

/**
 * ประกอบ body ของ `POST /v1/admin/employees` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * `employee_id` ถูก `trim()` ด้วยเหตุผลเดียวกับที่ `toRoleGrantBody()` ทำ: มันเป็น
 * business key ที่ **พิมพ์เข้ามาเอง** (D-039) ช่องว่างที่ติดมาจากการคัดลอกจะกลายเป็น
 * รหัสพนักงานที่พิมพ์ตามไม่ได้ตลอดไป
 *
 * `status` ที่เป็นสตริงว่างกลายเป็น `undefined` **ไม่ใช่สตริงว่างที่ถูกส่งออกไป** —
 * contract บอกว่ามันไม่บังคับและเซิร์ฟเวอร์เติม `ACTIVE` ให้
 */
export function toCreateEmployeeBody(
  employeeId: string,
  displayName: string,
  status: string,
): CreateEmployeeBody | null {
  const id = employeeId.trim();
  const name = displayName.trim();
  if (id === '' || name === '') {
    return null;
  }
  const trimmedStatus = status.trim();
  return {
    employee_id: id,
    display_name: name,
    ...(trimmedStatus === '' ? {} : { status: trimmedStatus }),
  };
}

/**
 * ประกอบ body ของ `POST .../departments` — คืน `null` เมื่อยังไม่ได้เลือกแผนก
 *
 * **การถอน (`granted: false`) ส่ง `is_primary: false` เสมอ** ไม่ว่าช่องติ๊กบนฟอร์ม
 * จะเป็นอะไร: contract บังคับให้ field นี้มีค่า แต่การถอนเป็นการลบแถวนั้นทิ้ง — การ
 * ส่ง `true` ไปด้วยคือการขอให้เซิร์ฟเวอร์ "ย้ายแผนกหลักมาที่แผนกที่กำลังจะหายไป"
 * ซึ่งเป็นคำขอที่อ่านไม่รู้เรื่องและจะสับสนเวลาอ่าน `rbac_audit_log` ย้อนหลัง
 */
export function toEmployeeDepartmentBody(
  departmentId: string,
  isPrimary: boolean,
  granted: boolean,
): SetEmployeeDepartmentBody | null {
  const trimmed = departmentId.trim();
  if (trimmed === '') {
    return null;
  }
  return {
    department_id: trimmed,
    is_primary: granted ? isPrimary : false,
    granted,
  };
}

/**
 * ประกอบ body ของ `POST .../plant-access` — คืน `null` เมื่อยังไม่ได้ระบุโรงงาน
 *
 * `plant_code` ถูก `trim()` ด้วยเหตุผลเดียวกับ `employee_id`: เป็น business code ที่
 * พิมพ์เข้ามาเอง และไม่มี endpoint ใดในกลุ่ม System Administration ที่ให้เลือกจาก
 * รายการได้ (contract ไม่มี `GET /admin/plants`) รหัสที่ไม่มีอยู่จริงจึงเป็น
 * `422 PLANT_NOT_FOUND` ที่หน้าจอต้องอธิบายให้ชัด ไม่ใช่กันไว้เองแบบเดา
 *
 * `is_home_plant` ถูกบังคับเป็น `false` ตอนถอนด้วยเหตุผลเดียวกับ `is_primary`
 */
export function toEmployeePlantAccessBody(
  plantCode: string,
  isHomePlant: boolean,
  granted: boolean,
): SetEmployeePlantAccessBody | null {
  const trimmed = plantCode.trim();
  if (trimmed === '') {
    return null;
  }
  return {
    plant_code: trimmed,
    is_home_plant: granted ? isHomePlant : false,
    granted,
  };
}

/**
 * แผนกหลักจากชุดที่เซิร์ฟเวอร์ส่งมา — `null` เมื่อไม่มีเลย
 *
 * **อ่านเท่านั้น ไม่ตัดสิน**: ถ้าชุดที่ส่งมามีสองแถวที่ `is_primary` เป็น `true`
 * (ซึ่ง partial unique index ทำให้เกิดไม่ได้) ฟังก์ชันนี้จะคืนแถวแรกโดยไม่ซ่อนอีก
 * แถว — `countPrimaryDepartments()` ด้านล่างมีไว้ให้เทสต์ยืนยันว่ามันเป็นหนึ่งเสมอ
 */
export function primaryDepartmentOf(
  departments: readonly EmployeeDepartmentLink[],
): EmployeeDepartmentLink | null {
  return departments.find((entry) => entry.is_primary) ?? null;
}

/** จำนวนแถวที่ `is_primary` — ต้องเป็น 0 หรือ 1 เสมอ ไม่มีทางเป็น 2 */
export function countPrimaryDepartments(
  departments: readonly EmployeeDepartmentLink[],
): number {
  return departments.filter((entry) => entry.is_primary).length;
}

/** โรงงานหลักจากชุดที่เซิร์ฟเวอร์ส่งมา — `null` เมื่อไม่มีเลย */
export function homePlantOf(
  grants: readonly EmployeePlantGrant[],
): EmployeePlantGrant | null {
  return grants.find((entry) => entry.is_home_plant) ?? null;
}

/** จำนวนแถวที่ `is_home_plant` — ต้องเป็น 0 หรือ 1 เสมอ */
export function countHomePlants(grants: readonly EmployeePlantGrant[]): number {
  return grants.filter((entry) => entry.is_home_plant).length;
}

/**
 * ชื่อแผนกหลักของพนักงานตามที่ **รายการองค์กร** รู้ — `null` คือ "ไม่สังกัดแผนกใด"
 *
 * `GET /admin/employees` บอกเพียงแผนก *หลัก* ไม่ใช่แผนกทั้งหมด นี่จึงเป็นสรุปของแถว
 * ในตาราง ไม่ใช่สถานะที่ใช้ตัดสินว่าจะกดปุ่มไหน — สถานะนั้นมาจากคำตอบของ `POST`
 */
export function summariseEmployeeDepartment(employee: Employee): string | null {
  return employee.primary_department_name;
}
